import { useState, useEffect } from "react";
import { useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query";
import styled from 'styled-components';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useMediaQuery } from 'react-responsive'

import { ControleExpeditieClient, ControleExpeditie, ControleExpeditieForListDto, KleurStatusEnum, OrderStatusEnum }
                        from "../client/procescontroleclient";
import Table from "../components/tables/Table";
import Button from "../components/UI/Button";
import PageTitle from "../components/UI/PageTitle";
import { ExpeditieButtonWrap, ExpeditieMobileButtonWrap } from '../components/UI/ButtonWrap';
import Input from "../components/UI/Input";
import Refresh from "../components/UI/Refresh";
import ProcesControleMobileCard, { Row, Item, Label, Field } from "../components/UI/ProcesControleMobileCard";
import MultiSelect from "../components/UI/InputMultiSelectSearch";
import Spinner from "../components/UI/Spinner";
import PageWrapper from "../components/UI/PageWrapper";
import { ButtonType } from "../Types";
import { getDateTimeFormat } from "../utilities/Utilities";
import { InputType, TableType } from '../Types';
import useDebounce from "../hooks/useDebounce";

interface Props {
  color: KleurStatusEnum;
}

const Tr = styled.tr<Props>`
    ${props => props.color===KleurStatusEnum.Red ? 'background-color: rgba(233,100,96,0.5)!important;' : null};
    ${props => props.color===KleurStatusEnum.DarkRed ? 'background-color: rgba(234,100,96,0.8)!important;' : null};
    ${props => props.color===KleurStatusEnum.Yellow ? 'background-color: rgba(240,255,0,0.5)!important;' : null};
    ${props => props.color===KleurStatusEnum.Orange ? `background-color: rgb(235 157 110 / 60%) !important;` : null};`;
const StyledButton = styled(Button)`
  align-self: end;
`;

export default function ProcesControleExpeditie() {
  const client = new ControleExpeditieClient(process.env.REACT_APP_APIURL);
  const [filter, setFilter] = useState<string>('');
  const [selectedLocations, setSelectedLocations] = useState<Array<string>>([]);
  const [selectedAfdelingen, setSelectedAfdelingen] = useState<Array<string>>([]);

  const isMobile = useMediaQuery({ query: '(max-width: 767px)' });
  const debouncedFilter = useDebounce(filter, 500);
  const queryClient = useQueryClient();

  const fetchExpeditie = ({ pageParam = 1 }) => 
  {
    const controleExpeditieForListDto = new ControleExpeditieForListDto();
    controleExpeditieForListDto.page = pageParam;
    controleExpeditieForListDto.selectedLocations = selectedLocations;
    controleExpeditieForListDto.selectedAfdelingen = selectedAfdelingen;
    controleExpeditieForListDto.filter = debouncedFilter;
    return client.getFilteredData(controleExpeditieForListDto);
  }

  const { data, fetchNextPage, hasNextPage, isLoading, isFetchingNextPage, refetch } 
  = useInfiniteQuery(["controleExpeditie"], fetchExpeditie, 
  {
    refetchOnWindowFocus: false,
    getNextPageParam: (lastPage) => lastPage.nextPage === 0 ? undefined : lastPage.nextPage,
  });

  const locationsQuery = useQuery(['controleLocations', 'all'], async () => {
    return await client.getLocations();
  }, {
    staleTime: Infinity
  });

  const afdelingenQuery = useQuery(['controleAfdelingen', 'all'], async () => {
    return await client.getAfdelingen();
  }, {
    staleTime: Infinity
  });

  useEffect(() => {
    refetch();
  }, [selectedLocations, selectedAfdelingen, debouncedFilter]);

  const refetchData = () => {
    queryClient.resetQueries();
    refetch();
    locationsQuery.refetch();
    afdelingenQuery.refetch();
  }

  useEffect(() => {
    const handleScroll = async (e: any) => {
      const {scrollHeight, scrollTop, clientHeight} = e.target.scrollingElement;
      if(!isFetchingNextPage && scrollHeight - scrollTop <= clientHeight * 1.2) {
        if(hasNextPage) await fetchNextPage()
      }
    }
    document.addEventListener('scroll', handleScroll)
    return () => {
      document.removeEventListener('scroll', handleScroll)
    }
  }, [fetchNextPage, hasNextPage])

  if (isLoading) {
    return <Spinner />;
  }

  return (
    <PageWrapper>
      <PageTitle>Expeditie Controle</PageTitle>
      <ExpeditieButtonWrap>
        {!isMobile ? <StyledButton disabled={isLoading} buttonType={ButtonType.Primary} spacingR svg 
        onClick={refetchData}><FontAwesomeIcon icon="eye" />Verversen</StyledButton>
          : <Refresh onClick={refetchData} />}
        <ExpeditieMobileButtonWrap>
          <div>
            <MultiSelect
              options={locationsQuery.data?.map(location => {
                return {
                  value: location,
                  label: location
                };
              })}
              name="locations"
              onChange={(field: any, value: any) => setSelectedLocations(value)}
              value={selectedLocations}
              isDisabled={false}
              isAutoFocus={false}
              label={'Banduitgang'}
            />
            </div>
            <div>
            <MultiSelect
              options={afdelingenQuery.data?.map(afdeling => {
                return {
                  value: afdeling,
                  label: afdeling
                };
              })}
              name="afdelingen"
              onChange={(field: any, value: any) => setSelectedAfdelingen(value)}
              value={selectedAfdelingen}
              isDisabled={false}
              isAutoFocus={false}
              label={'Afdeling'}
            />
          </div>
          <div>
          <Input
            inputType={InputType.SearchInput}
            onChange={(e: React.FormEvent<HTMLInputElement>) => setFilter(e.currentTarget.value)}
            value={filter}
          />
        </div>
        </ExpeditieMobileButtonWrap>
      </ExpeditieButtonWrap>
      {!isMobile ?
        <Table tableType={TableType.Default} stickymodal>
          <thead>
            <tr>
              <th>Banduitgang</th>
              <th>Debnr</th>
              <th>Hub</th>
              <th>VPartijnummer</th>
              <th>Locatie</th>
              <th>Status</th>
              <th>Afdeling</th>
              <th>Omschrijving</th>
              <th style={{ textAlign: 'right' }}>Kolli</th>
              <th style={{ textAlign: 'right' }}>Inh kolli</th>
              <th style={{ textAlign: 'right' }}>Los</th>
              <th style={{ textAlign: 'right' }}>Stuks</th>
              <th>Doosnrs</th>
              <th style={{ textAlign: 'right' }}>Lengte</th>
              <th>Picktijd</th>
              <th>In kubustijd</th>
              <th>Inpaktijd</th>
            </tr>
          </thead>
          <tbody>
            {data?.pages.map((pages: any) => {
              return pages?.expeditieRows.map((row: ControleExpeditie, index: number) => {
                return (
              <Tr key={index} color={row.kleurStatus}>
                <td>{row.banduitgang}</td>
                <td>{row.debiteurnummer}</td>
                <td>{row.hub}</td>
                <td>{row.vPartijnummer}</td>
                <td>{row.pickLocatie}</td>
                <td>{row.orderStatus === OrderStatusEnum.Expeditie ? "" : OrderStatusEnum[row.orderStatus]}</td>
                <td>{row.afdeling}</td>
                <td>{row.omschrijving}</td>
                <td style={{ textAlign: 'right' }}>{row.kolli}</td>
                <td style={{ textAlign: 'right' }}>{row.kolliInhoud}</td>
                <td style={{ textAlign: 'right' }}>{row.los}</td>
                <td style={{ textAlign: 'right' }}>{row.stuks}</td>
                <td>{row.boxNumbers}</td>
                <td style={{ textAlign: 'right' }}>{row.lengte}</td>
                <td>{getDateTimeFormat(row.picktijd)}</td>
                <td>{getDateTimeFormat(row.scanKubusIntijd)}</td>
                <td>{getDateTimeFormat(row.inpaktijd)}</td>
              </Tr>
            )})
          })}
          </tbody>
        </Table> :
        data?.pages.map((pages: any) => {
          return pages?.expeditieRows.map((row: ControleExpeditie, index: number) => {
            return (
              <ProcesControleMobileCard color={row.kleurStatus}
              compactView={
                <>
                  <div><strong>St.tot.</strong>{row.afdeling}</div>
                  <div><strong>Omschr.</strong>{row.omschrijving.substring(0, 15)}{row.omschrijving.length >= 15 && '..'}</div>
                </>
              }
              expandView={
                <>
                  <Row>
                    <Item><Label>Omschrijving</Label><Field>{row.omschrijving}</Field></Item>
                  </Row>
                  <Row>
                    <Item>
                      <Label>Hub</Label><Field>{row.hub}</Field>
                    </Item>
                    <Item>
                      <Label>Afdeling</Label><Field>{row.afdeling}</Field>
                    </Item>
                  </Row>
                  <Row>
                    <Item>
                      <Label>Kolli</Label><Field>{row.kolli}</Field>
                    </Item>
                    <Item>
                      <Label>Inh kolli</Label><Field>{row.kolliInhoud}</Field>
                    </Item>
                  </Row>
                  <Row>
                    <Item>
                      <Label>Los</Label><Field>{row.los}</Field>
                    </Item>
                    <Item>
                      <Label>Stuks</Label><Field>{row.stuks}</Field>
                    </Item>
                    <Item>
                      <Label>Lengte</Label><Field>{row.lengte}</Field>
                    </Item>
                  </Row>
                  <Row>
                    <Item>
                      <Label>Picktijd</Label><Field>{getDateTimeFormat(row.picktijd)}</Field>
                    </Item>
                    <Item>
                      <Label>In kubustijd</Label><Field>{getDateTimeFormat(row.scanKubusIntijd)}</Field>
                    </Item>
                    </Row>
                  <Row>
                    <Item>
                      <Label>Inpaktijd</Label><Field>{getDateTimeFormat(row.inpaktijd)}</Field>
                    </Item>
                    <Item>
                      <Label></Label><Field></Field>
                    </Item>
                  </Row>
                  <Row>
                    <Item>
                      <Label>vPartNr</Label><Field>{row.vPartijnummer}</Field>
                    </Item>
                    <Item>
                      <Label>Pick.Loc.</Label><Field>{row.pickLocatie}</Field>
                    </Item>
                    <Item>
                      <Label>Doosnrs</Label><Field>{row.boxNumbers}</Field>
                    </Item>
                  </Row>
                </>
              }
              title={row.debiteurnummer}
              subtitle={row.hub}
              checked={undefined}
            />
          )})
        })}
        {isFetchingNextPage && <Spinner />}
    </PageWrapper>
  );
};
